import { useState } from 'react';
import { Link } from 'react-router';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import Logo from '@/components/Logo';
import { EventDateModal } from '@/components/EventDateModal';
import { useArrangement } from '@/context/ArrangementContext';
import { getReminder } from '@/lib/reminders';
import { cn } from '@/lib/utils';

export function Page({ className, children }: { className?: string; children: React.ReactNode }) {
  return <main className={cn('min-w-0 flex-1 bg-surface', className)}>{children}</main>;
}

export function StandalonePage({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <div className="flex shrink-0 flex-col lg:flex-row">
        <div className="flex h-[61px] shrink-0 items-center bg-navy px-4 sm:px-6 lg:h-auto lg:w-60 lg:px-5">
          <Link to="/">
            <Logo variant="light" height={26} className="h-[26px] w-auto lg:h-[52px]" />
          </Link>
        </div>
        <div className="flex-1">
          <PageHeader title={title} subtitle={subtitle} countdown="never" />
        </div>
      </div>
      <main className="flex-1">{children}</main>
    </div>
  );
}

function countdownLabel(daysLeft: number | null) {
  if (daysLeft === null) return 'Datoen er ikke satt ennå';
  if (daysLeft < 0) return 'Arrangementet har vært!';
  if (daysLeft === 0) return 'I dag er dagen!';
  return `Hurra, kun ${daysLeft} ${daysLeft === 1 ? 'dag' : 'dager'} igjen!`;
}

export function PageHeader({
  title,
  subtitle,
  countdown = 'desktop',
}: {
  title: string;
  subtitle?: string;
  countdown?: 'always' | 'desktop' | 'never';
}) {
  const { arrangement } = useArrangement();
  const [editingDate, setEditingDate] = useState(false);

  return (
    <header className="flex shrink-0 flex-col bg-white lg:min-h-[81px] lg:flex-row lg:items-stretch lg:justify-between">
      <h1 className="flex flex-col justify-center px-4 py-4 text-[15px] leading-snug text-navy sm:px-6 lg:px-8">
        <span className="font-bold uppercase">{title} </span>
        {subtitle && <span className="font-light">{subtitle}</span>}
      </h1>
      {editingDate && <EventDateModal onClose={() => setEditingDate(false)} />}
      {arrangement && countdown !== 'never' && (
        <div className={cn('grid-cols-2 lg:flex', countdown === 'always' ? 'grid' : 'hidden')}>
          <p className="flex items-center justify-center bg-sky px-4 py-3 text-center text-xs font-bold text-navy sm:text-[15px] lg:px-12">
            {countdownLabel(getReminder(arrangement.date).daysLeft)}
          </p>
          <div className="flex flex-col justify-center bg-navy px-4 py-3 leading-snug text-krem-lys lg:min-w-72 lg:px-8">
            <span className="text-xs font-bold uppercase sm:text-[15px]">Den store dagen</span>
            <span className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs font-light sm:text-[15px]">
              {arrangement.date ? format(new Date(arrangement.date), 'dd. MMMM yyyy', { locale: nb }) : 'Ikke bestemt'}
              <button
                type="button"
                onClick={() => setEditingDate(true)}
                className="cursor-pointer text-[11px] font-normal text-white underline"
              >
                {arrangement.date ? 'Endre dato' : 'Sett dato'}
              </button>
            </span>
          </div>
        </div>
      )}
    </header>
  );
}

export function Loading({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex flex-1 items-center justify-center gap-2 py-16 text-sm text-muted-foreground', className)}
    >
      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
      Laster…
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div className="min-h-dvh flex bg-surface">
      <Loading />
    </div>
  );
}

export function PageContent({
  className,
  children,
  loading = false,
  ...rest
}: { className?: string; children: React.ReactNode; loading?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="mx-auto w-full max-w-[1224px] p-4 pb-25 sm:p-6 lg:px-10 lg:py-8 xl:px-20">
      <div className={cn('flex flex-col gap-5', className)} aria-busy={loading} {...rest}>
        {loading ? <Loading /> : children}
      </div>
    </div>
  );
}

export function SectionHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <h2 className="text-[15px] font-bold uppercase text-navy">{title}</h2>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}
